import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, IconButton, Alert,
  CircularProgress, Snackbar, Grid, Box, Typography, Divider, Chip,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Inventory as InventoryIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS, SHIFT_OPTIONS } from '../../config';
import {
  PRODUCTS, GRADES, BRANDS, PACK_SIZES, PACK_STYLES,
  loadProductsFromDatabase, loadGradesFromDatabase,
  loadBrandsFromDatabase, loadPackSizesFromDatabase,
} from '../../utils/DROPDOWN_OPTIONS';

// ============================================================
// AddLineEntryModal — Phase 2A.2
// ------------------------------------------------------------
// 2-section "line + pallets" modal:
//   Section 1: 13 group-key fields (filled once, shared across pallets)
//   Section 2: 1+ pallet rows (Pallet ID, Cartons, Loose Bags, Loose KGs, CS1, CS2)
//
// On submit, builds N entries by spreading {...header, ...pallet} and
// POSTs to /api/inventory.php with { entries: [...] }.
// inventory.php POST already handles atomic multi-row insert.
//
// Status is locked to PRODUCED (fresh production entries only).
// Returns from gate-out processing will use a separate flow.
//
// Location: frontend/src/components/modals/AddLineEntryModal.js
// ============================================================

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

// JS port of inventory.php calculateWeights() — used for read-only display of
// per-carton and per-pallet totals while the user is still typing. Server
// recomputes these on save (source of truth).
const parsePackSize = (packSize) => {
  if (!packSize) return null;
  const m = String(packSize).match(/(\d+\.?\d*)\s*[Xx/]\s*(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/i);
  if (!m) return null;
  const bagsPerCarton = parseFloat(m[1]) || 0;
  const weightPerBag = parseFloat(m[2]) || 0;
  const unit = (m[3] || 'LB').toUpperCase();
  return { bagsPerCarton, weightPerBag, unit };
};

// Returns { kg, lb } per single carton based on pack_size alone
const perCartonWeight = (packSize) => {
  const p = parsePackSize(packSize);
  if (!p) return { kg: 0, lb: 0 };
  const weightPerCartonInUnit = p.bagsPerCarton * p.weightPerBag;
  let lb;
  if (p.unit.includes('OZ')) lb = weightPerCartonInUnit / 16;
  else if (p.unit.includes('KG')) lb = weightPerCartonInUnit * 2.20;
  else lb = weightPerCartonInUnit;
  const kg = lb / 2.20;
  return { kg, lb };
};

// KG ↔ LB conversion factor (matches inventory.php convention: 1 KG = 2.20 LB).
// User enters KG only; LB columns are derived in render via this constant.
const KG_TO_LB = 2.20;

// Status options for ENTRY (not gate-out states). These are statuses an item
// can have when arriving in / returning to the cold store.
//
// IMPORTANT: REPROCESS-IN and QC-SAMPLE-IN must be added to the inventory.status
// enum via SQL migration BEFORE these options will save successfully. See
// inventory_status_enum_migration.sql for the migration.
const ENTRY_STATUS_OPTIONS = [
  { value: 'PRODUCED',        label: 'PRODUCED — Fresh production' },
  { value: 'REPACK-IN',       label: 'REPACK-IN — Returning after repack' },
  { value: 'REPROCESS-IN',    label: 'REPROCESS-IN — Returning after reprocess' },
  { value: 'QC-SAMPLE-IN',    label: 'QC-SAMPLE-IN — Returning after QC sampling' },
  { value: 'ONLINE MIX',      label: 'ONLINE MIX — Mixed CS stock + fresh' },
  { value: 'EXCESS-RETURNED', label: 'EXCESS-RETURNED — Returned from FCL excess' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyPallet = () => ({
  pallet_id: '',
  cartons: '',
  wt_per_carton_kg: '',
  // wt_per_carton_lb is NOT stored — derived: wt_per_carton_kg × KG_TO_LB
  loose_bags: '0',
  loose_per_bag_kg: '0',
  // loose_per_bag_lb is NOT stored — derived: loose_per_bag_kg × KG_TO_LB
  cs1: '',
  cs2: '',
});

const emptyHeader = () => ({
  status: 'PRODUCED',
  production_date: todayISO(),
  shift: 'DAY',
  product: '',
  grade: '',
  pack_style: 'FINAL',
  pack_size: '',
  brand: '',
  product_id: '',
  lot_no: '',
  day_code: '',
  trace_id: '',
  cs_selection: 'CS1', // 'CS1' or 'CS2' — controls which BIN column is enabled
});

export default function AddLineEntryModal({ open, onClose, onSuccess }) {
  const [header, setHeader] = useState(emptyHeader());
  const [pallets, setPallets] = useState([emptyPallet()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [opts, setOpts] = useState({ products: [], grades: [], brands: [], pack_sizes: [] });
  const [snackbar, setSnackbar] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setHeader(emptyHeader());
      setPallets([emptyPallet()]);
      setError('');
    }
  }, [open]);

  // Load dropdowns once
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadProductsFromDatabase(),
      loadGradesFromDatabase(),
      loadBrandsFromDatabase(),
      loadPackSizesFromDatabase(),
    ]).then(() => {
      if (cancelled) return;
      setOpts({
        products: [...PRODUCTS].sort(),
        grades: [...GRADES].sort(),
        brands: [...BRANDS].sort(),
        pack_sizes: [...PACK_SIZES].sort(),
      });
    });
    return () => { cancelled = true; };
  }, []);

  // ---- handlers ----
  const setHF = (key, value) => setHeader(prev => ({ ...prev, [key]: value }));

  // Plain pallet-field setter. LB columns are derived in render (KG × 2.20),
  // so no sync logic needed here.
  const setPF = (idx, key, value) => {
    setPallets(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  };
  const addPallet = () => setPallets(prev => [...prev, emptyPallet()]);
  const removePallet = (idx) => setPallets(prev =>
    prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)
  );

  // ---- per-carton weight (derived from pack_size in header) ----
  const perCartonReadout = useMemo(
    () => perCartonWeight(header.pack_size),
    [header.pack_size]
  );

  // When the user picks/changes Pack Size, auto-fill empty wt/carton KG fields
  // on every pallet row from the parsed pack_size. Don't overwrite values
  // the user has already manually entered. LB renders as KG × 2.20 derived.
  useEffect(() => {
    if (perCartonReadout.kg <= 0) return;
    setPallets(prev => prev.map(p => {
      const next = { ...p };
      if (!String(next.wt_per_carton_kg || '').trim()) {
        next.wt_per_carton_kg = perCartonReadout.kg.toFixed(2);
      }
      return next;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCartonReadout.kg]);

  // ---- validation ----
  const validation = useMemo(() => {
    const hMissing = [];
    const requiredHeaderKeys = [
      'status', 'production_date', 'shift', 'product', 'grade', 'pack_style',
      'pack_size', 'brand', 'product_id', 'lot_no', 'day_code', 'trace_id',
    ];
    requiredHeaderKeys.forEach(k => {
      if (!String(header[k] || '').trim()) hMissing.push(k);
    });

    const palletErrors = pallets.map(p => {
      const errs = [];
      if (!String(p.pallet_id || '').trim()) errs.push('pallet_id');
      const c = parseInt(p.cartons, 10);
      if (isNaN(c) || c < 0) errs.push('cartons');
      return errs;
    });

    // Duplicate pallet IDs within submit
    const ids = pallets.map(p => String(p.pallet_id || '').trim()).filter(Boolean);
    const dupSet = new Set();
    const seen = new Set();
    ids.forEach(id => { if (seen.has(id)) dupSet.add(id); else seen.add(id); });

    const hasErrors = hMissing.length > 0 ||
      palletErrors.some(e => e.length > 0) || dupSet.size > 0;

    return { hMissing, palletErrors, duplicates: dupSet, hasErrors };
  }, [header, pallets]);

  // ---- summary ----
  const summary = useMemo(() => {
    const totalCartons = pallets.reduce((s, p) => s + (parseInt(p.cartons, 10) || 0), 0);
    const totalLooseBags = pallets.reduce((s, p) => s + (parseInt(p.loose_bags, 10) || 0), 0);
    const totalCartonKg = pallets.reduce((s, p) => {
      return s + ((parseInt(p.cartons, 10) || 0) * (parseFloat(p.wt_per_carton_kg) || 0));
    }, 0);
    const totalLooseKg = pallets.reduce((s, p) => {
      return s + ((parseInt(p.loose_bags, 10) || 0) * (parseFloat(p.loose_per_bag_kg) || 0));
    }, 0);
    // LB totals are derived from KG totals using fixed 1 KG = 2.20 LB
    const totalCartonLb = totalCartonKg * KG_TO_LB;
    const totalLooseLb  = totalLooseKg  * KG_TO_LB;
    return {
      palletCount: pallets.length,
      totalCartons, totalLooseBags,
      totalCartonKg, totalCartonLb,
      totalLooseKg, totalLooseLb,
      grandKg: totalCartonKg + totalLooseKg,
      grandLb: totalCartonLb + totalLooseLb,
    };
  }, [pallets]);

  // ---- submit ----
  const handleSubmit = async () => {
    if (validation.hasErrors) {
      setError('Please fix the highlighted fields before saving.');
      return;
    }

    // Build N entries by spreading header onto each pallet
    const entries = pallets.map(p => {
      const cartons = parseInt(p.cartons, 10) || 0;
      const looseBags = parseInt(p.loose_bags, 10) || 0;
      const wtPerCartonKg = parseFloat(p.wt_per_carton_kg) || 0;
      const loosePerBagKg = parseFloat(p.loose_per_bag_kg) || 0;

      // Per-pallet totals — KG is source of truth, LB derived via 1 KG = 2.20 LB.
      // Sent as explicit overrides so the server doesn't overwrite from pack_size.
      const totalCartonKg = cartons * wtPerCartonKg;
      const totalLooseKg  = looseBags * loosePerBagKg;
      const total_kgs    = +(totalCartonKg + totalLooseKg).toFixed(2);
      const total_pounds = +(total_kgs * KG_TO_LB).toFixed(2);

      return {
        // Header fields (shared)
        status: header.status,
        production_date: header.production_date,
        shift: header.shift,
        product: header.product,
        grade: header.grade,
        pack_style: header.pack_style,
        pack_size: header.pack_size,
        brand: header.brand,
        product_id: header.product_id,
        nav_id: '', // server auto-fills via getNavIdFromDB()
        lot_no: header.lot_no,
        trace_id: header.trace_id,
        day_code: header.day_code,
        // Pallet fields
        pallet_id: String(p.pallet_id).trim(),
        cartons,
        loose_bags: looseBags,
        // The DB has a single `loose_kgs` column representing total loose weight.
        loose_kgs: +totalLooseKg.toFixed(2),
        // Explicit weight overrides — patched inventory.php honors these
        total_kgs,
        total_pounds,
        // CS bin: only the selected one carries a value
        cs1: header.cs_selection === 'CS1' ? String(p.cs1 || '').trim() : '',
        cs2: header.cs_selection === 'CS2' ? String(p.cs2 || '').trim() : '',
        remarks: '',
      };
    });

    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        API_ENDPOINTS.INVENTORY,
        { entries },
        getAuthConfig()
      );

      if (res.data?.success !== false) {
        const insertedCount = res.data?.insertedIds?.length || entries.length;
        setSnackbar({
          severity: 'success',
          msg: `✅ Added ${insertedCount} pallet${insertedCount === 1 ? '' : 's'} successfully`,
        });
        // close + bubble up
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 1200);
      } else {
        setError(res.data?.error || 'Failed to save entries');
      }
    } catch (err) {
      console.error('Bulk add failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save entries');
    } finally {
      setLoading(false);
    }
  };

  // ---- render helpers ----
  const fieldErr = (key) => validation.hMissing.includes(key);
  const palletFieldErr = (idx, key) => validation.palletErrors[idx]?.includes(key);
  const isDup = (id) => id && validation.duplicates.has(String(id).trim());

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(90deg, #C0392B 0%, #8E44AD 50%, #1F618D 100%)',
          color: 'white', fontWeight: 700, py: 1.5,
        }}>
          📦 Add Cold Store Entry — New Line + Pallets
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* ── SECTION 1: HEADER ── */}
          <Typography variant="overline" sx={{ color: '#8E44AD', fontWeight: 700, letterSpacing: 0.6 }}>
            Line Header — applies to all pallets in this entry
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2, mt: 1, mb: 3,
          }}>
            <TextField
              fullWidth size="small" type="date"
              label="Production Date *" required
              InputLabelProps={{ shrink: true }}
              value={header.production_date}
              onChange={(e) => setHF('production_date', e.target.value)}
              error={fieldErr('production_date')}
            />
            <TextField
              fullWidth size="small" select
              label="Shift *" required
              value={header.shift}
              onChange={(e) => setHF('shift', e.target.value)}
              error={fieldErr('shift')}
            >
              {SHIFT_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth size="small" select
              label="Status *" required
              value={header.status}
              onChange={(e) => setHF('status', e.target.value)}
              error={fieldErr('status')}
              helperText="Use REPACK-IN / EXCESS-RETURNED / ONLINE MIX for items returning to CS"
              FormHelperTextProps={{ sx: { mx: 0, fontSize: 10.5 } }}
            >
              {ENTRY_STATUS_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth size="small"
              label="Product ID *" required
              value={header.product_id}
              onChange={(e) => setHF('product_id', e.target.value)}
              error={fieldErr('product_id')}
              placeholder="e.g. 25360-3-A-1"
            />

            <TextField
              fullWidth size="small" select
              label="Product *" required
              value={header.product}
              onChange={(e) => setHF('product', e.target.value)}
              error={fieldErr('product')}
            >
              {opts.products.length === 0 && <MenuItem value="" disabled>Loading…</MenuItem>}
              {opts.products.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth size="small" select
              label="Grade *" required
              value={header.grade}
              onChange={(e) => setHF('grade', e.target.value)}
              error={fieldErr('grade')}
            >
              {opts.grades.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth size="small" select
              label="Pack Style *" required
              value={header.pack_style}
              onChange={(e) => setHF('pack_style', e.target.value)}
              error={fieldErr('pack_style')}
            >
              {PACK_STYLES.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth size="small" select
              label="Pack Size *" required
              value={header.pack_size}
              onChange={(e) => setHF('pack_size', e.target.value)}
              error={fieldErr('pack_size')}
            >
              {opts.pack_sizes.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>

            <TextField
              fullWidth size="small" select
              label="Brand *" required
              value={header.brand}
              onChange={(e) => setHF('brand', e.target.value)}
              error={fieldErr('brand')}
            >
              {opts.brands.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth size="small"
              label="Lot No *" required
              value={header.lot_no}
              onChange={(e) => setHF('lot_no', e.target.value)}
              error={fieldErr('lot_no')}
              placeholder="e.g. CTC-3761"
            />
            <TextField
              fullWidth size="small"
              label="Day Code *" required
              value={header.day_code}
              onChange={(e) => setHF('day_code', e.target.value)}
              error={fieldErr('day_code')}
              placeholder="e.g. 6D26"
            />
            <TextField
              fullWidth size="small"
              label="Trace ID *" required
              value={header.trace_id}
              onChange={(e) => setHF('trace_id', e.target.value)}
              error={fieldErr('trace_id')}
              placeholder="e.g. AP051085/002"
            />
          </Box>

          {/* NAV ID note + CS selection + per-carton readout */}
          <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption">
              <strong>NAV ID</strong> will be auto-assigned on save based on Product + Brand + Grade + Pack Size.
            </Typography>
          </Alert>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
            p: 1.5, mb: 2, bgcolor: '#fafbfc',
            border: '1px solid #e0e3e8', borderRadius: 1,
          }}>
            <FormControl>
              <FormLabel sx={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: '#8E44AD !important',
              }}>
                Cold Store *
              </FormLabel>
              <RadioGroup
                row
                value={header.cs_selection}
                onChange={(e) => setHF('cs_selection', e.target.value)}
              >
                <FormControlLabel
                  value="CS1"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8E44AD' } }} />}
                  label="CS1"
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  value="CS2"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8E44AD' } }} />}
                  label="CS2"
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ borderLeft: '1px solid #e0e3e8', pl: 3 }}>
              <Typography variant="caption" sx={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: '#6b7280', display: 'block',
              }}>
                Per Carton (from Pack Size)
              </Typography>
              {perCartonReadout.kg > 0 ? (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                  {perCartonReadout.kg.toFixed(2)} kg
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    / {perCartonReadout.lb.toFixed(2)} lb
                  </Typography>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
                  Pick a Pack Size
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* ── SECTION 2: PALLETS ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="overline" sx={{ color: '#8E44AD', fontWeight: 700, letterSpacing: 0.6 }}>
              Pallets ({pallets.length})
            </Typography>
            <Button
              size="small" startIcon={<AddIcon />} onClick={addPallet}
              variant="outlined"
              sx={{
                color: '#8E44AD', borderColor: '#8E44AD',
                '&:hover': { borderColor: '#6c3483', bgcolor: 'rgba(142,68,173,0.04)' },
              }}
            >
              Add Pallet
            </Button>
          </Box>

          {/* Pallet rows table - 12 data columns, horizontal scroll on overflow */}
          <Box sx={{
            border: '1px solid #e0e3e8', borderRadius: 1,
            overflow: 'auto', // horizontal + vertical scroll when content exceeds
            maxHeight: 420,
          }}>
            <Box component="table" sx={{
              borderCollapse: 'collapse', fontSize: 12.5,
              minWidth: 1280, // ensures all 14 cols are reachable; container scrolls horizontally
            }}>
              <Box component="thead" sx={{
                bgcolor: '#8E44AD',
                position: 'sticky', top: 0, zIndex: 1,
              }}>
                <Box component="tr">
                  <Box component="th" sx={{ ...thSx, width: 32 }}>#</Box>
                  <Box component="th" sx={{ ...thSx, width: 110 }}>PALLET ID *</Box>
                  <Box component="th" sx={{ ...thSx, width: 80, textAlign: 'right' }}>CARTONS *</Box>
                  <Box component="th" sx={{ ...thSx, width: 90, textAlign: 'right' }}>WT/CARTON (KG)</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 90 }}>WT/CARTON (LB)</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 100 }}>TOTAL CARTON (KG)</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 100 }}>TOTAL CARTON (LB)</Box>
                  <Box component="th" sx={{ ...thSx, width: 80, textAlign: 'right' }}>LOOSE BAGS</Box>
                  <Box component="th" sx={{ ...thSx, width: 80, textAlign: 'right' }}>LOOSE KGS</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 80 }}>LOOSE LB</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 105 }}>TOT.LOOSE (KG)</Box>
                  <Box component="th" sx={{ ...thCalcSx, width: 105 }}>TOT.LOOSE (LB)</Box>
                  <Box component="th" sx={{ ...thSx, width: 110 }}>
                    {header.cs_selection} (BIN)
                  </Box>
                  <Box component="th" sx={{ ...thSx, width: 44, textAlign: 'center' }}>—</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {pallets.map((p, idx) => {
                  const cartons = parseInt(p.cartons, 10) || 0;
                  const looseBags = parseInt(p.loose_bags, 10) || 0;
                  const wtCKg = parseFloat(p.wt_per_carton_kg) || 0;
                  const lpKg = parseFloat(p.loose_per_bag_kg) || 0;
                  // LB derived on the fly via fixed 1 KG = 2.20 LB
                  const wtCLb = wtCKg * KG_TO_LB;
                  const lpLb = lpKg * KG_TO_LB;
                  const totalCartonKg = cartons * wtCKg;
                  const totalCartonLb = totalCartonKg * KG_TO_LB;
                  const totalLooseKg = looseBags * lpKg;
                  const totalLooseLb = totalLooseKg * KG_TO_LB;
                  const csKey = header.cs_selection === 'CS1' ? 'cs1' : 'cs2';
                  return (
                    <Box component="tr" key={idx}
                         sx={{ '&:nth-of-type(even)': { bgcolor: '#fafbfc' } }}>
                      <Box component="td" sx={tdSx}>
                        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', fontSize: 12 }}>
                          {idx + 1}
                        </Typography>
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth
                          value={p.pallet_id}
                          onChange={(e) => setPF(idx, 'pallet_id', e.target.value)}
                          error={palletFieldErr(idx, 'pallet_id') || isDup(p.pallet_id)}
                          helperText={isDup(p.pallet_id) ? 'Duplicate' : ''}
                          FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }}
                          inputProps={{ style: { fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth type="number"
                          value={p.cartons}
                          onChange={(e) => setPF(idx, 'cartons', e.target.value)}
                          error={palletFieldErr(idx, 'cartons')}
                          inputProps={{ min: 0, style: { textAlign: 'right', fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth type="number"
                          value={p.wt_per_carton_kg}
                          onChange={(e) => setPF(idx, 'wt_per_carton_kg', e.target.value)}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdDerivedSx}>
                        {wtCLb > 0 ? wtCLb.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdCalcSx}>
                        {totalCartonKg > 0 ? totalCartonKg.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdCalcSx}>
                        {totalCartonLb > 0 ? totalCartonLb.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth type="number"
                          value={p.loose_bags}
                          onChange={(e) => setPF(idx, 'loose_bags', e.target.value)}
                          inputProps={{ min: 0, style: { textAlign: 'right', fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth type="number"
                          value={p.loose_per_bag_kg}
                          onChange={(e) => setPF(idx, 'loose_per_bag_kg', e.target.value)}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdDerivedSx}>
                        {lpLb > 0 ? lpLb.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdCalcSx}>
                        {totalLooseKg > 0 ? totalLooseKg.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdCalcSx}>
                        {totalLooseLb > 0 ? totalLooseLb.toFixed(2) : '—'}
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <TextField
                          size="small" fullWidth
                          value={p[csKey]}
                          onChange={(e) => setPF(idx, csKey, e.target.value)}
                          placeholder={header.cs_selection === 'CS1' ? 'e.g. 3A1-2' : 'e.g. 5B3-1'}
                          inputProps={{ style: { fontSize: 12.5 } }}
                        />
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <IconButton
                          size="small" disabled={pallets.length === 1}
                          onClick={() => removePallet(idx)}
                          title={pallets.length === 1 ? 'At least one pallet required' : 'Remove this pallet'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Helper hint about how the row works */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            Tip: pick a Pack Size in the header to auto-fill WT/CARTON KG across all pallets — you can override per row. LB columns auto-calculate from KG using 1 KG = 2.20 LB.
          </Typography>

          {/* Summary footer */}
          <Box sx={{
            mt: 2, p: 1.5, bgcolor: '#FEF5E7', borderRadius: 1,
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            border: '1px solid #f5d99b',
          }}>
            <InventoryIcon sx={{ color: '#8E44AD' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {summary.palletCount} pallet{summary.palletCount === 1 ? '' : 's'}
                {' · '}{summary.totalCartons.toLocaleString()} cartons
                {' · '}{summary.totalLooseBags.toLocaleString()} loose bags
              </Typography>
              {(summary.grandKg > 0 || summary.grandLb > 0) && (
                <Typography variant="caption" sx={{ color: '#1F618D', fontFamily: 'ui-monospace, Consolas, monospace' }}>
                  Carton: {summary.totalCartonKg.toFixed(2)} kg / {summary.totalCartonLb.toFixed(2)} lb
                  {summary.totalLooseKg > 0 && (
                    <> {' · '}Loose: {summary.totalLooseKg.toFixed(2)} kg / {summary.totalLooseLb.toFixed(2)} lb</>
                  )}
                  {' · '}<strong>Total: {summary.grandKg.toFixed(2)} kg / {summary.grandLb.toFixed(2)} lb</strong>
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Totals saved to DB exactly as shown
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || validation.hasErrors}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            sx={{
              background: 'linear-gradient(90deg, #C0392B 0%, #8E44AD 50%, #1F618D 100%)',
              fontWeight: 600,
              '&:hover': { opacity: 0.92 },
              '&.Mui-disabled': { opacity: 0.5, color: 'white' },
            }}
          >
            {loading
              ? 'Saving…'
              : `Save ${pallets.length} Pallet${pallets.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity || 'info'} variant="filled" sx={{ fontWeight: 600 }}>
            {snackbar.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </>
  );
}

// ---- shared cell styles ----
const thSx = {
  color: 'white', fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4,
  textAlign: 'left', padding: '8px 10px',
  borderBottom: 0, whiteSpace: 'nowrap',
};
// Auto-calc column header — slightly different shade to distinguish from input columns
const thCalcSx = {
  ...thSx,
  bgcolor: '#6c3483',
  textAlign: 'right',
  fontStyle: 'italic',
};
const tdSx = {
  padding: '6px 8px',
  borderBottom: '1px solid #e0e3e8',
  verticalAlign: 'top',
};
// Per-unit derived cell (e.g. WT/CARTON LB) — read-only, lighter than totals
const tdDerivedSx = {
  ...tdSx,
  textAlign: 'right',
  color: '#6b7280',
  fontFamily: 'ui-monospace, Consolas, monospace',
  bgcolor: '#fbf7fc',
  fontSize: 12.5,
  fontWeight: 500,
};
// Total auto-calc cell (e.g. TOTAL CARTON KG/LB, TOT.LOOSE KG/LB) — bolder
const tdCalcSx = {
  ...tdSx,
  textAlign: 'right',
  color: '#1F618D',
  fontWeight: 700,
  fontFamily: 'ui-monospace, Consolas, monospace',
  bgcolor: '#f5edf7',
  fontSize: 12.5,
};
